export interface BrandingConfig {
  displayName: string
  logoUrl: string | null
  primaryColor: string
  accentColor: string
  textColor: string
  bgColor: string
  highlightColor: string
}

export const PRAGMELIS_BRANDING: BrandingConfig = {
  displayName: 'Pragmelis 365',
  logoUrl: null,
  primaryColor:   '#1E2A4A',
  accentColor:    '#2E5FA3',
  textColor:      '#4A5568',
  bgColor:        '#F7F7F5',
  highlightColor: '#E8EDF7',
}

function hexToHsl(hex: string): string {
  try {
    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const l = (max + min) / 2
    let h = 0
    let s = 0
    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
        case g: h = ((b - r) / d + 2) / 6; break
        case b: h = ((r - g) / d + 4) / 6; break
      }
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
  } catch {
    return ''
  }
}

let currentBranding: BrandingConfig | null = null

/**
 * Applique (ou réapplique) le branding stocké en mémoire selon le mode actif.
 *
 * - En mode CLAIR, on injecte la `--primary`/`--accent` revendeur en inline style.
 * - En mode SOMBRE, on RETIRE l'inline style pour laisser la cascade `.dark { ... }`
 *   définie dans index.css opérer — sinon les inline styles, plus spécifiques que
 *   toute classe CSS, écrasent les valeurs dark et rendent les titres invisibles.
 *
 * Les couleurs neutres (--background, --foreground, --muted, --secondary,
 * --muted-foreground) ne sont jamais injectées : elles sont gouvernées exclusivement
 * par index.css (clair vs `.dark`).
 */
function reapplyBranding(): void {
  if (!currentBranding || typeof document === 'undefined') return
  const root = document.documentElement
  const isDark = root.classList.contains('dark')

  if (isDark) {
    root.style.removeProperty('--primary')
    root.style.removeProperty('--accent')
  } else {
    const primary = hexToHsl(currentBranding.primaryColor)
    const accent  = hexToHsl(currentBranding.accentColor)
    if (primary) { root.style.setProperty('--primary', primary) }
    if (accent)  { root.style.setProperty('--accent',  accent) }
  }

  // Purge dans tous les cas (héritage d'anciennes versions de cette fonction)
  root.style.removeProperty('--background')
  root.style.removeProperty('--foreground')
  root.style.removeProperty('--muted')
  root.style.removeProperty('--muted-foreground')
  root.style.removeProperty('--secondary')
}

export function applyBranding(b: BrandingConfig): void {
  currentBranding = b
  reapplyBranding()
}

// Réapplique le branding à chaque changement de thème (la classe `.dark` détermine
// si on injecte ou non les couleurs revendeur en inline style).
if (typeof window !== 'undefined') {
  window.addEventListener('pragmelis:theme-changed', reapplyBranding)
}
